import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { TrendingUp, Wallet, Clock } from 'lucide-react';

// Realistic mix of Indian + Global names with masked surnames for social proof
const NAMES = [
  'Rohan S.', 'Aisha K.', 'Priya M.', 'Karthik R.', 'Aman J.', 'Sneha P.', 'Rahul D.', 'Neha V.',
  'Arjun T.', 'Anjali B.', 'Vikram N.', 'Pooja G.', 'Siddharth M.', 'Riya C.', 'Yash A.', 'Tanvi S.',
  'Manish K.', 'Ishaan R.', 'Aditya P.', 'Kavya N.', 'Dhruv J.', 'Meera S.', 'Aryan G.', 'Nikhil B.',
  'Daniel W.', 'Carlos V.', 'Sofia L.', 'Lucas M.', 'Emma R.', 'Olivia K.', 'Ahmed Z.', 'Yusuf K.',
  'Marco D.', 'Elena F.', 'Hassan A.', 'James P.', 'Liam B.', 'Noah S.', 'Mia T.', 'Hiroshi T.',
  'Chen W.', 'Mateo G.', 'Isabella R.', 'Ethan L.',
];

const CITIES = [
  'Mumbai', 'Delhi', 'Bangalore', 'Hyderabad', 'Pune', 'Chennai', 'Kolkata', 'Ahmedabad',
  'Jaipur', 'Lucknow', 'Surat', 'Indore', 'London', 'Dubai', 'Singapore', 'Madrid',
  'Berlin', 'Toronto', 'Lisbon', 'Cairo', 'Sydney', 'Tokyo',
];

const randInt = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;
const randomFromArr = (arr) => arr[Math.floor(Math.random() * arr.length)];
const randomAmount = () => {
  // Weighted distribution: most $200-$1500, occasionally $2k-$5k for spike effect
  const r = Math.random();
  if (r < 0.7) return (Math.random() * 1300 + 200).toFixed(2);   // $200-$1500
  if (r < 0.95) return (Math.random() * 1500 + 1500).toFixed(2); // $1500-$3000
  return (Math.random() * 2500 + 3000).toFixed(2);               // $3000-$5500
};

const LivePayoutTicker = () => {
  const [event, setEvent] = useState(null);

  useEffect(() => {
    let showTimeout = null;
    let hideTimeout = null;
    let cancelled = false;

    const trigger = () => {
      if (cancelled) return;
      // Show the popup
      setEvent({
        id: Date.now(),
        name: randomFromArr(NAMES),
        city: randomFromArr(CITIES),
        amount: randomAmount(),
        minutesAgo: randInt(1, 14),
      });
      // Hide after exactly 2s
      hideTimeout = setTimeout(() => {
        if (cancelled) return;
        setEvent(null);
        // Wait 7-15s before triggering next popup
        const nextDelay = randInt(7000, 15000);
        showTimeout = setTimeout(trigger, nextDelay);
      }, 2000);
    };

    // First popup after 4s
    showTimeout = setTimeout(trigger, 4000);
    return () => {
      cancelled = true;
      if (showTimeout) clearTimeout(showTimeout);
      if (hideTimeout) clearTimeout(hideTimeout);
    };
  }, []);

  return (
    <div className="fixed bottom-4 left-4 sm:bottom-6 sm:left-6 z-40 pointer-events-none max-w-[calc(100vw-2rem)] sm:max-w-[340px]" data-testid="live-payout-ticker">
      <AnimatePresence mode="wait">
        {event && (
          <motion.div
            key={event.id}
            initial={{ opacity: 0, x: -40, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: -40, scale: 0.95 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="pointer-events-auto relative"
          >
            {/* gradient halo */}
            <div className="absolute -inset-px bg-gradient-to-br from-brand-positive/40 via-brand-primary/20 to-transparent rounded-2xl blur opacity-50" />
            <div className="relative bg-[#0A0F1C]/95 backdrop-blur-xl border border-white/10 rounded-2xl p-3.5 shadow-[0_15px_45px_rgba(0,0,0,0.6)] flex items-center gap-3 overflow-hidden">
              {/* shimmer */}
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.04] to-transparent translate-x-[-100%] animate-[shimmer_1.2s_ease-out]" />
              {/* avatar */}
              <div className="relative flex-shrink-0">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-positive/25 to-brand-primary/15 border border-brand-positive/30 flex items-center justify-center">
                  <Wallet className="w-4 h-4 text-brand-positive" strokeWidth={2} />
                </div>
                <span className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-brand-positive opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-positive border border-[#0A0F1C]" />
                </span>
              </div>
              {/* text */}
              <div className="flex-1 min-w-0 relative">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-white text-sm font-bold truncate">{event.name}</span>
                  <span className="text-[10px] text-zinc-500 whitespace-nowrap">{event.minutesAgo}m ago</span>
                </div>
                <div className="text-[11px] text-zinc-400 leading-tight">
                  got a payout of <strong className="text-brand-positive font-mono">${Number(event.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</strong>
                </div>
                <div className="flex items-center gap-1.5 mt-1 text-[9px] text-zinc-500 uppercase tracking-wider">
                  <span>{event.city}</span><span>·</span>
                  <span className="flex items-center gap-0.5"><Clock className="w-2.5 h-2.5" /> Avg payout 1.2 h</span>
                  <TrendingUp className="w-2.5 h-2.5 text-brand-positive ml-auto" />
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      <style>{`
        @keyframes shimmer { 0% { transform: translateX(-100%); } 100% { transform: translateX(100%); } }
      `}</style>
    </div>
  );
};

export default LivePayoutTicker;
